import { usePathHelper } from '@/hooks/usePathHelper'
import { Link } from '@tanstack/react-router'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { SnapdragonDownloadButton } from '@/components/integrations/snapdragon/SnapdragonConnectionCards'
import SaveBar from '@/components/forms/SaveBar'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { InfoNotice, OrganizationContextNotice } from '@/components/patterns/info-notice'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Typography } from '@/components/ui/typography'
import { BetaFeatureKey, hasBetaFeature } from '@/lib/beta-features'
import { Can, useIsReadOnly } from '@/lib/casl'
import { useAppForm } from '@/lib/form'
import {
  advanceLocalPrintProfileRevision,
  clearLocalPrintProfile,
  connectLocalPrintStation,
  createLocalPrintProfileRevisionGuard,
  getLocalPrintHealth,
  getLocalPrintPermissionState,
  isLocalPrintProfileWriteInvalidated,
  loadLocalPrintProfile,
  saveLocalPrintWorkstationProfile,
  serializeLocalPrintWorkstationMutation,
  subscribeToLocalPrintProfileUpdates,
  syncLocalPrintWorkstation,
} from '@/lib/local-print'
import {
  organizationQueries,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import {
  snapdragonQueries,
  useDisconnectSnapdragonComputerMutation,
} from '@/lib/services/snapdragon/snapdragon.query'
import { snapdragonWorkstationFormSchema } from '@/lib/services/snapdragon/snapdragon.schema'
import { getErrorMessage } from '@/lib/utils'
import { createFileRoute, notFound, useParams } from '@tanstack/react-router'
import { CheckCircle2, Laptop, Plug, RefreshCw, Unplug } from 'lucide-react'
import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react'
import { toast } from 'sonner'
import { PrinterConfiguration } from './-printer-configuration'
import { SnapdragonSetupWizard } from './-setup-wizard'

export const Route = createFileRoute('/_authenticated/$orgId/settings/integrations/snapdragon/')({
  component: SnapdragonSetupPage,
  loader: async ({ context, params }) => {
    if (!context.ability.can('list', 'snapdragon-settings')) throw notFound()

    const organization = await context.queryClient.ensureQueryData(
      organizationQueries.show(params.orgId)
    )

    if (!hasBetaFeature(organization, BetaFeatureKey.Snapdragon)) throw notFound()

    await Promise.all([
      context.queryClient.ensureQueryData(
        snapdragonQueries.printers({ organizationId: params.orgId })
      ),
      context.queryClient.ensureQueryData(
        snapdragonQueries.salesOrderAutoPrintSettings({ organizationId: params.orgId })
      ),
    ])

    return { isReadOnly: !context.ability.can('edit', 'snapdragon-settings') }
  },
})

function workstationFormDefaults(computer) {
  return {
    name: computer?.workstationName ?? '',
    location_id: computer?.locationId ? String(computer.locationId) : 'none',
  }
}

function ComputerSettingsForm({
  computer,
  locations,
  organizationId,
  organizationName,
  isReadOnly,
  wizard,
  editor = false,
  isCurrent = () => true,
  onDisconnect,
  onSaving,
  onSaved,
}) {
  const locationOptions = [
    { id: 'none', name: 'No location selected' },
    ...locations.map((location) => ({ id: String(location.id), name: location.name })),
  ]
  const form = useAppForm({
    defaultValues: workstationFormDefaults(computer),
    validators: {
      onSubmit: snapdragonWorkstationFormSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        if (isReadOnly) return

        try {
          onSaving?.()
          const response = await saveLocalPrintWorkstationProfile(organizationId, computer, {
            name: value.name.trim(),
            locationId: value.location_id === 'none' ? null : Number(value.location_id),
          })
          if (!isCurrent()) return
          const nextLocations = response.locations ?? locations

          formApi.reset(workstationFormDefaults(response.profile))
          toast.success('Computer settings saved')
          onSaved(response.profile, nextLocations)
        } catch (error) {
          if (!isCurrent() || isLocalPrintProfileWriteInvalidated(error)) return
          toast.error('Could not save computer settings', {
            description: getErrorMessage(error),
          })

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  if (editor) {
    return (
      <form.AppForm>
        <form.Form className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <form.AppField name="name">
              {(field) => (
                <field.TextInput
                  id="snapdragon-computer-name"
                  label="Computer name"
                  disabled={isReadOnly}
                />
              )}
            </form.AppField>
            <form.AppField name="location_id">
              {(field) => (
                <field.SelectInput
                  id="snapdragon-location"
                  label="Location"
                  options={locationOptions}
                  disabled={isReadOnly}
                />
              )}
            </form.AppField>
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-border pt-5 sm:flex-row sm:items-center sm:justify-between">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="ghostMuted" disabled={isReadOnly}>
                  <Unplug className="size-4" />
                  Disconnect
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Disconnect this computer?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Snapdragon will stop working on this computer. Other connected computers will
                    not be affected.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onDisconnect}>Disconnect</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {!isReadOnly ? (
              <SaveBar layout="inline" renderInPortal={false} submitButtonText="Save changes" />
            ) : null}
          </div>
        </form.Form>
      </form.AppForm>
    )
  }

  return (
    <form.AppForm>
      <form.Form className={wizard ? 'flex min-h-0 flex-1 flex-col gap-5' : 'space-y-3'}>
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1">
                <CardTitle>{wizard ? 'Computer details' : 'This computer'}</CardTitle>
                <CardDescription>
                  {wizard
                    ? 'Name this computer and choose the shop location where it is used.'
                    : 'Once connected, this browser can use the printers on this computer.'}
                </CardDescription>
              </div>
              <Badge variant="success" size="status">
                <CheckCircle2 /> Connected
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <Typography variant="label" className="block">
                  {computer.workstationName || computer.stationName || 'This computer'}
                </Typography>
                <Typography variant="supporting" className="block">
                  Connected to {organizationName}
                </Typography>
              </div>

              {!wizard ? (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="ghostMuted" disabled={isReadOnly}>
                      <Unplug className="size-4" />
                      Disconnect
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Disconnect this computer?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Snapdragon will stop working on this computer. Other connected computers
                        will not be affected.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={onDisconnect}>Disconnect</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              ) : null}
            </div>

            <div className="grid gap-4 border-t border-border pt-5 sm:grid-cols-2">
              <form.AppField name="name">
                {(field) => (
                  <field.TextInput
                    id="snapdragon-computer-name"
                    label="Computer name"
                    disabled={isReadOnly}
                  />
                )}
              </form.AppField>
              <form.AppField name="location_id">
                {(field) => (
                  <field.SelectInput
                    id="snapdragon-location"
                    label="Location"
                    options={locationOptions}
                    disabled={isReadOnly}
                  />
                )}
              </form.AppField>
            </div>
          </CardContent>
        </Card>

        {!isReadOnly ? (
          <div className={wizard ? 'mt-auto' : 'contents'}>
            <SaveBar
              forceDirty={wizard}
              layout={wizard ? 'inline' : 'fixed'}
              renderInPortal={!wizard}
              submitButtonText={wizard ? 'Save and continue' : 'Save'}
            />
          </div>
        ) : null}
      </form.Form>
    </form.AppForm>
  )
}

function DirectPrintingAccessNotice({ isReadOnly, state, message, isPairing, onContinue }) {
  const connecting = state === 'checking' || isPairing
  const actionLabel = connecting
    ? 'Connecting…'
    : state === 'idle'
      ? 'Continue'
      : state === 'pairing_required'
        ? 'Pair this browser'
        : 'Try again'

  return (
    <InfoNotice
      title={connecting ? 'Checking this computer' : 'Connect this browser to Snapdragon'}
      icon={<Laptop />}
      tone={message && state !== 'idle' ? 'warning' : 'info'}
      action={
        <Button type="button" disabled={isReadOnly || connecting} onClick={onContinue}>
          {connecting ? (
            <RefreshCw className="size-4 animate-spin" />
          ) : state === 'pairing_required' ? (
            <Plug className="size-4" />
          ) : null}
          {actionLabel}
        </Button>
      }
    >
      {message ||
        (connecting ? (
          <>Confirming the saved connection and loading printers from Snapdragon.</>
        ) : (
          <>
            Chrome needs access to connect this browser to Snapdragon. Click Continue, then choose
            Allow when Chrome prompts you.
          </>
        ))}
    </InfoNotice>
  )
}

function SnapdragonSetupPage() {
  const { orgId } = useParams({ strict: false })
  const path = usePathHelper()
  const { data: organization } = useOrganization(orgId)
  const { isReadOnly } = useIsReadOnly()
  const disconnectMutation = useDisconnectSnapdragonComputerMutation()
  const [initialLocalProfile] = useState(() => loadLocalPrintProfile(orgId))
  const hasSavedLocalPairing = Boolean(initialLocalProfile.token)
  const [computer, setComputer] = useState(hasSavedLocalPairing ? initialLocalProfile : null)
  const [localStation, setLocalStation] = useState(null)
  const [state, setState] = useState(hasSavedLocalPairing ? 'checking' : 'idle')
  const [message, setMessage] = useState('')
  const [computerEditorOpen, setComputerEditorOpen] = useState(false)
  const [localPrintProfileRevision, setLocalPrintProfileRevision] = useState(0)
  const [locations, setLocations] = useState([])
  const [setupStep, setSetupStep] = useState('computer')
  const [wizardRequestedForOrgId, setWizardRequestedForOrgId] = useState('')
  const [localSetupRequestedForOrgId, setLocalSetupRequestedForOrgId] = useState(
    hasSavedLocalPairing ? String(orgId) : ''
  )
  const [isPairing, setIsPairing] = useState(false)
  const activeOrganizationIdRef = useRef(String(orgId))
  // oxlint-disable-next-line react/refs -- grandfathered
  activeOrganizationIdRef.current = String(orgId)
  const computerCheckRef = useRef(null)
  const localCheckGenerationRef = useRef(0)
  const localPrintSessionControllerRef = useRef(new AbortController())
  const localSetupRequested = localSetupRequestedForOrgId === String(orgId)
  const renewLocalPrintSession = useCallback(() => {
    localPrintSessionControllerRef.current.abort()
    localPrintSessionControllerRef.current = new AbortController()
  }, [])
  const invalidateLocalComputerSession = useCallback(() => {
    localCheckGenerationRef.current += 1
    renewLocalPrintSession()
    computerCheckRef.current?.controller.abort()
    computerCheckRef.current = null
  }, [renewLocalPrintSession])

  const checkCurrentComputer = useCallback(
    async ({ refreshConnection = false, openSetupOnConnect = false } = {}) => {
      const checkGeneration = localCheckGenerationRef.current
      const checkOrganizationId = String(orgId)
      const activeCheck = computerCheckRef.current
      if (activeCheck?.generation === checkGeneration && !activeCheck.controller.signal.aborted) {
        return activeCheck.promise
      }
      activeCheck?.controller.abort()

      let profileRevisionIsCurrent = createLocalPrintProfileRevisionGuard(orgId)
      const controller = new AbortController()
      const checkIsCurrent = () =>
        !controller.signal.aborted &&
        checkGeneration === localCheckGenerationRef.current &&
        activeOrganizationIdRef.current === checkOrganizationId
      const profileCheckIsCurrent = () => checkIsCurrent() && profileRevisionIsCurrent()
      const check = (async () => {
        try {
          const health = await getLocalPrintHealth({
            signal: controller.signal,
            // Chrome holds this request open until the user answers its local-access prompt.
            timeoutMs: openSetupOnConnect ? null : 3_000,
          })
          if (!profileCheckIsCurrent()) return false

          if (health.protocolVersion !== 1) {
            setComputer(null)
            setComputerEditorOpen(false)
            setState('update_required')
            setMessage('Update Snapdragon to connect this computer.')
            return false
          }
          const {
            profile: pairedProfile,
            profileRevision,
            station,
          } = await connectLocalPrintStation({
            organizationId: orgId,
            organizationName: organization.name,
            health,
            refreshConnection,
            shouldPersist: profileCheckIsCurrent,
            signal: controller.signal,
            timeoutMs: 3_000,
          })
          if (!checkIsCurrent()) return false
          if (Number.isInteger(profileRevision)) {
            profileRevisionIsCurrent = createLocalPrintProfileRevisionGuard(orgId, profileRevision)
          }
          if (!profileCheckIsCurrent()) return false

          if (!station?.connected) throw new Error('This computer is not connected.')

          const profile = pairedProfile
          let workstation
          try {
            workstation = await syncLocalPrintWorkstation({
              organizationId: orgId,
              profile,
              station,
              shouldPersist: profileCheckIsCurrent,
            })
          } catch (error) {
            if (!profileCheckIsCurrent()) return false

            setComputer(null)
            setComputerEditorOpen(false)
            setState('registration_error')
            setMessage(
              `Snapdragon is connected, but OneStem could not finish setting up this computer. ${getErrorMessage(error)}`
            )
            return false
          }
          if (!profileCheckIsCurrent()) return false

          setComputer(workstation.profile)
          setLocations(workstation.locations ?? [])
          setLocalStation(station)
          setState('connected')
          setMessage('')
          if (openSetupOnConnect && workstation.profile.setupCompleted !== true) {
            setSetupStep('computer')
            setWizardRequestedForOrgId(String(orgId))
          }
          return true
        } catch (error) {
          if (!profileCheckIsCurrent()) return false

          setComputer(null)
          setComputerEditorOpen(false)
          const permissionState =
            error instanceof TypeError ? await getLocalPrintPermissionState() : 'unknown'
          if (!profileCheckIsCurrent()) return false

          const canRefreshExistingConnection =
            error?.code === 'pairing_refresh_failed' ||
            error?.code === 'pairing_refresh_limited' ||
            error?.code === 'pairing_refresh_unavailable' ||
            (!refreshConnection && (error?.code === 'shop_not_linked' || error?.httpStatus === 403))
          setState(
            permissionState === 'denied'
              ? 'permission_denied'
              : canRefreshExistingConnection
                ? 'pairing_required'
                : 'not_connected'
          )
          setMessage(
            permissionState === 'denied'
              ? "Chrome has blocked access to Snapdragon. Open this site's controls in the address bar, allow Apps on device, then try again."
              : error instanceof TypeError
                ? 'OneStem could not reach Snapdragon on this computer. Make sure Snapdragon is open, then try again. If Chrome asks to find devices, choose Allow.'
                : canRefreshExistingConnection
                  ? 'Snapdragon is connected. Pair this browser to use its printers.'
                  : 'OneStem could not connect this browser to Snapdragon. Make sure Snapdragon is open, then try again.'
          )
          return false
        }
      })()

      const checkEntry = { controller, generation: checkGeneration, promise: check }
      computerCheckRef.current = checkEntry
      try {
        return await check
      } finally {
        if (computerCheckRef.current === checkEntry) computerCheckRef.current = null
      }
    },
    [orgId, organization.name]
  )

  const checkCurrentOrganizationComputer = useEffectEvent((options) =>
    checkCurrentComputer(options)
  )

  useEffect(() => {
    invalidateLocalComputerSession()
    const savedProfile = loadLocalPrintProfile(orgId)
    const hasSavedPairing = Boolean(savedProfile.token)

    // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
    setComputer(hasSavedPairing ? savedProfile : null)
    setLocalStation(null)
    setLocations([])
    setComputerEditorOpen(false)
    setWizardRequestedForOrgId('')
    setSetupStep('computer')
    setLocalSetupRequestedForOrgId(hasSavedPairing ? String(orgId) : '')
    setState(hasSavedPairing ? 'checking' : 'idle')
    setMessage('')
    setIsPairing(false)

    if (hasSavedPairing) void checkCurrentOrganizationComputer()
  }, [invalidateLocalComputerSession, orgId])

  useEffect(
    () =>
      subscribeToLocalPrintProfileUpdates(orgId, ({ type, source }) => {
        if (type !== 'cleared') return

        invalidateLocalComputerSession()
        setWizardRequestedForOrgId('')
        setSetupStep('computer')
        setComputer(null)
        setComputerEditorOpen(false)
        setLocations([])
        setLocalStation(null)
        setState('disconnected')
        setMessage(
          source === 'storage'
            ? 'This computer was disconnected in another tab.'
            : 'This computer is disconnected from this shop.'
        )
      }),
    [invalidateLocalComputerSession, orgId]
  )

  useEffect(() => {
    if (!localSetupRequested || state !== 'connected') return undefined

    const interval = window.setInterval(() => {
      void checkCurrentComputer()
    }, 60_000)
    return () => window.clearInterval(interval)
  }, [checkCurrentComputer, localSetupRequested, state])

  useEffect(() => {
    if (localPrintSessionControllerRef.current.signal.aborted) {
      localPrintSessionControllerRef.current = new AbortController()
    }

    return () => {
      localPrintSessionControllerRef.current.abort()
      computerCheckRef.current?.controller.abort()
      computerCheckRef.current = null
    }
  }, [])

  const handleStartDirectPrinting = async () => {
    const refreshConnection = state === 'pairing_required'

    if (!localSetupRequested) {
      invalidateLocalComputerSession()
      setLocalSetupRequestedForOrgId(String(orgId))
      setComputer(null)
      setComputerEditorOpen(false)
    }

    setState('checking')
    setMessage('')
    setIsPairing(true)
    try {
      await checkCurrentComputer({ refreshConnection, openSetupOnConnect: true })
    } finally {
      if (activeOrganizationIdRef.current === String(orgId)) setIsPairing(false)
    }
  }

  const handleDisconnect = async () => {
    if (!computer?.stationId) return
    const disconnectOrganizationId = orgId
    const disconnectComputerId = computer.computerId
    const disconnectStationId = computer.stationId
    const mutationGeneration = localCheckGenerationRef.current
    try {
      const { cleared, updateActiveUi } = await serializeLocalPrintWorkstationMutation(
        disconnectOrganizationId,
        disconnectComputerId,
        async () => {
          const profileRevisionIsCurrent =
            createLocalPrintProfileRevisionGuard(disconnectOrganizationId)
          await disconnectMutation.mutateAsync({
            organizationId: disconnectOrganizationId,
            stationId: disconnectStationId,
          })
          const profileStillCurrent = profileRevisionIsCurrent()
          const updateCurrentOrganization =
            profileStillCurrent &&
            mutationBelongsToCurrentOrganization() &&
            mutationGeneration === localCheckGenerationRef.current
          const pairingCleared = profileStillCurrent
            ? clearLocalPrintProfile(disconnectOrganizationId, disconnectComputerId)
            : true

          return { cleared: pairingCleared, updateActiveUi: updateCurrentOrganization }
        }
      )
      if (!updateActiveUi) return

      invalidateLocalComputerSession()
      setWizardRequestedForOrgId('')
      setComputerEditorOpen(false)
      setSetupStep('computer')
      setComputer(null)
      setLocations([])
      setLocalStation(null)
      setState('disconnected')
      setMessage('This computer is disconnected from this shop.')
      toast.success('This computer is disconnected')
      if (!cleared) {
        toast.error('Browser data could not be cleared', {
          description: "Clear this site's app data before connecting another computer.",
        })
      }
    } catch (error) {
      if (!mutationBelongsToCurrentOrganization()) return
      toast.error('Could not disconnect this computer', { description: getErrorMessage(error) })
    }
  }

  const handleCloudDisconnected = () => {
    invalidateLocalComputerSession()
    setIsPairing(false)
    setLocalSetupRequestedForOrgId('')
    setWizardRequestedForOrgId('')
    setSetupStep('computer')
    setComputer(null)
    setComputerEditorOpen(false)
    setLocations([])
    setLocalStation(null)
    setState('disconnected')
    setMessage('')
  }

  const connected = state === 'connected' && computer
  const setupRequired = connected && computer.setupCompleted !== true
  const setupAvailable = setupRequired && !isReadOnly
  const showSetupWizard = setupAvailable && wizardRequestedForOrgId === String(orgId)
  const handleSetUpLater = () => {
    setWizardRequestedForOrgId('')
  }
  const handleContinueSetup = () => {
    setWizardRequestedForOrgId(String(orgId))
  }
  const mutationBelongsToCurrentOrganization = () =>
    activeOrganizationIdRef.current === String(orgId)
  const markWorkstationProfileChanged = () => {
    if (!mutationBelongsToCurrentOrganization()) return false
    advanceLocalPrintProfileRevision(orgId)
    return true
  }
  const handleComputerSaved = (profile, nextLocations) => {
    if (!markWorkstationProfileChanged()) return false
    setComputer(profile)
    setLocations(nextLocations)
    if (setupStep === 'computer') setSetupStep('pos-receipt')
    return true
  }
  const handleLocalPrintProfileSaved = (profile) => {
    if (!markWorkstationProfileChanged()) return
    setComputer(profile)
    setLocalPrintProfileRevision((revision) => revision + 1)
  }

  const handleWizardBack = () => {
    setSetupStep('computer')
  }

  const handleWizardStepComplete = async (savedProfile) => {
    if (!markWorkstationProfileChanged()) return
    setLocalPrintProfileRevision((revision) => revision + 1)
    if (savedProfile) setComputer(savedProfile)

    try {
      const finalProfile = savedProfile ?? computer
      const response = await saveLocalPrintWorkstationProfile(orgId, finalProfile, {
        setupCompleted: true,
      })
      if (!mutationBelongsToCurrentOrganization()) return

      markWorkstationProfileChanged()
      setWizardRequestedForOrgId('')
      setComputer(response.profile)
      setSetupStep('computer')
      toast.success('This computer is ready')
    } catch (error) {
      if (!mutationBelongsToCurrentOrganization() || isLocalPrintProfileWriteInvalidated(error)) {
        return
      }
      toast.error('Could not finish setting up this computer', {
        description: getErrorMessage(error),
      })
    }
  }

  const directPrintingStatus = connected ? null : (
    <DirectPrintingAccessNotice
      isReadOnly={isReadOnly}
      state={state}
      message={message}
      isPairing={isPairing}
      onContinue={handleStartDirectPrinting}
    />
  )

  const mainContent = (
    <IfGlobalOrg
      show={false}
      fallback={
        <OrganizationContextNotice>
          Snapdragon is organization-specific. Set it up inside the florist account that will use
          this computer.
        </OrganizationContextNotice>
      }
    >
      <PrinterConfiguration
        key={`${orgId}:configuration`}
        organizationId={orgId}
        organizationName={organization.name}
        isReadOnly={isReadOnly}
        directPrintingStatus={directPrintingStatus}
        workstationName={
          connected ? computer.workstationName || computer.stationName || 'This computer' : ''
        }
        onEditWorkstation={() => setComputerEditorOpen(true)}
        localPrintEnabled={Boolean(connected)}
        localPrintSuspended={Boolean(showSetupWizard)}
        localPrintProfile={connected ? computer : null}
        localPrintStation={localStation}
        localPrintProfileRevision={localPrintProfileRevision}
        localSetupRequired={Boolean(setupRequired)}
        onContinueLocalSetup={handleContinueSetup}
        onRequestLocalConnection={handleStartDirectPrinting}
        onCloudDisconnected={handleCloudDisconnected}
        onLocalPrintProfileSaving={markWorkstationProfileChanged}
        onLocalPrintProfileSaved={handleLocalPrintProfileSaved}
        isLocalPrintProfileCurrent={mutationBelongsToCurrentOrganization}
        // oxlint-disable-next-line react/refs -- grandfathered
        localPrintSignal={localPrintSessionControllerRef.current.signal}
      />

      <Dialog open={showSetupWizard} onOpenChange={(open) => !open && handleSetUpLater()}>
        <DialogContent
          size="3xlarge"
          viewport="contained"
          padding="none"
          spacing="none"
          className="grid grid-rows-[minmax(0,1fr)]"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Set up receipt printing</DialogTitle>
            <DialogDescription>
              Identify this computer and choose its receipt printer.
            </DialogDescription>
          </DialogHeader>
          <SnapdragonSetupWizard step={setupStep} onSetUpLater={handleSetUpLater}>
            {setupStep === 'computer' ? (
              <ComputerSettingsForm
                key={`${orgId}:${computer?.computerId}:wizard`}
                computer={computer}
                locations={locations}
                organizationId={orgId}
                organizationName={organization.name}
                isReadOnly={isReadOnly}
                wizard
                isCurrent={mutationBelongsToCurrentOrganization}
                onDisconnect={handleDisconnect}
                onSaving={markWorkstationProfileChanged}
                onSaved={handleComputerSaved}
              />
            ) : (
              <PrinterConfiguration
                organizationId={orgId}
                organizationName={organization.name}
                isReadOnly={isReadOnly}
                setupStep={setupStep}
                onSetupBack={handleWizardBack}
                onSetupStepComplete={handleWizardStepComplete}
                onLocalPrintProfileSaving={markWorkstationProfileChanged}
                isLocalPrintProfileCurrent={mutationBelongsToCurrentOrganization}
                // oxlint-disable-next-line react/refs -- grandfathered
                localPrintSignal={localPrintSessionControllerRef.current.signal}
              />
            )}
          </SnapdragonSetupWizard>
        </DialogContent>
      </Dialog>

      <Dialog open={computerEditorOpen} onOpenChange={setComputerEditorOpen}>
        <DialogContent size="large" viewport="scroll">
          <DialogHeader>
            <DialogTitle>Edit this computer</DialogTitle>
            <DialogDescription>
              Change the name or shop location used for this computer.
            </DialogDescription>
          </DialogHeader>
          {connected ? (
            <ComputerSettingsForm
              key={`${orgId}:${computer.computerId}:editor`}
              computer={computer}
              locations={locations}
              organizationId={orgId}
              organizationName={organization.name}
              isReadOnly={isReadOnly}
              wizard={false}
              editor
              isCurrent={mutationBelongsToCurrentOrganization}
              onDisconnect={handleDisconnect}
              onSaving={markWorkstationProfileChanged}
              onSaved={(profile, nextLocations) => {
                if (handleComputerSaved(profile, nextLocations)) setComputerEditorOpen(false)
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </IfGlobalOrg>
  )

  return (
    <div className="space-y-5">
      <div className="max-w-6xl">
        <AdminPageHeader
          icon={Laptop}
          title="Snapdragon"
          description="Choose what prints for online and POS orders."
          actions={
            <div className="flex flex-wrap gap-2">
              <Can I="edit" a="super-ticket-settings" passThrough>
                {(canEdit, ability) =>
                  (canEdit || ability.can('view', 'sales-orders')) && (
                    <Button asChild variant="outline">
                      <Link {...path.supertickets.index().link}>Superticket layouts</Link>
                    </Button>
                  )
                }
              </Can>
              <SnapdragonDownloadButton organizationId={orgId} />
            </div>
          }
        />
      </div>

      <div className="max-w-6xl space-y-5">{mainContent}</div>
    </div>
  )
}
